// The "current round" of a document's comment thread: the messages that became
// visible after the document's latest approval (submissions.reviewed_at). Lives
// in @l2u/shared because the API (both submissions payloads + the vendor reply
// guard) and the vendor client must agree on it, and the API is a Worker that
// cannot import @l2u/ui.
//
// "Visible" depends on who is looking:
// - The VENDOR sees an admin comment only at publish (`published_at`), because
//   a comment is news when Notify sends it, not when the reviewer drafts it. A
//   comment drafted before an approval but published in the same Notify is
//   therefore in the new round; one published at an earlier Notify is not.
//   A vendor comment is visible from creation.
// - The ADMIN sees `published_at ?? created_at`: an unpublished draft reply is
//   already the admin's answer from the admin's side, so it counts from when it
//   was written.
//
// Callers map their thread rows to `RoundMessage` with the side's timestamp as
// `at` and drop rows the side cannot see (vendorVisibleAt === null).

export type ThreadRow = {
  author_type: 'admin' | 'vendor';
  created_at: string;
  published_at: string | null;
};

export type RoundMessage = { author_type: 'admin' | 'vendor'; at: string };

/** When the VENDOR first sees a thread row: an admin comment at publish (null = not yet visible), a vendor comment at creation. */
export function vendorVisibleAt(row: ThreadRow): string | null {
  return row.author_type === 'admin' ? row.published_at : row.created_at;
}

/** When the ADMIN sees it: a draft counts from creation, so an unpublished reply still reads as the admin's answer. */
export function adminVisibleAt(row: ThreadRow): string {
  return row.published_at ?? row.created_at;
}

/**
 * The latest of any number of ISO stamps (null/undefined = absent; none → null).
 * Used to floor the VENDOR's round at max(reviewed_at, reverify_requested_at,
 * conversation_closed_at): a renewal upload is the vendor's answer to every
 * comment before it, so a reviewer comment published before the upload no
 * longer leaves the vendor owing a reply; one published after it still does.
 * An admin closing the conversation ends the round the same way. It also floors
 * the vendor reply door and the ADMIN round at max(reviewed_at,
 * conversation_closed_at) (see conversationFloor).
 */
export function laterStamp(...stamps: (string | null | undefined)[]): string | null {
  let latest: string | null = null;
  for (const s of stamps) {
    if (s != null && (latest === null || s > latest)) latest = s;
  }
  return latest;
}

/** The submission stamps a round floor reads. A missing conversation_closed_at (older API, fixture) means "not closed". */
export type RoundFloorFields = {
  reviewed_at: string | null;
  reverify_requested_at?: string | null;
  conversation_closed_at?: string | null;
};

/** Floors `current_round_latest_author` on the VENDOR payload: latest of approval, renewal upload and close. */
export function vendorTurnFloor(s: RoundFloorFields): string | null {
  return laterStamp(s.reviewed_at, s.reverify_requested_at, s.conversation_closed_at);
}

/** Floors the vendor reply door (`current_round_has_admin_comment`, the me.ts POST guard) and the ADMIN payload's `current_round_latest_author`: latest of approval and close. */
export function conversationFloor(s: RoundFloorFields): string | null {
  return laterStamp(s.reviewed_at, s.conversation_closed_at);
}

/** Messages that became visible after the latest approval. reviewedAt null → every message. ISO strings compare with `>` (same convention as hasPublishedRework). */
export function currentRound<T extends RoundMessage>(
  messages: readonly T[],
  reviewedAt: string | null,
): T[] {
  if (reviewedAt == null) return [...messages];
  return messages.filter((m) => m.at > reviewedAt);
}

/** The author of the in-round message with the greatest `at` (ties: last in input order); null for an empty round. */
export function currentRoundLatestAuthor(
  messages: readonly RoundMessage[],
  reviewedAt: string | null,
): 'admin' | 'vendor' | null {
  let latest: RoundMessage | null = null;
  for (const m of currentRound(messages, reviewedAt)) {
    if (latest === null || m.at >= latest.at) latest = m;
  }
  return latest?.author_type ?? null;
}

/** Whether the reviewer has spoken this round. Stays true after the vendor replies, so follow-ups are allowed. */
export function currentRoundHasAdminMessage(
  messages: readonly RoundMessage[],
  reviewedAt: string | null,
): boolean {
  return currentRound(messages, reviewedAt).some((m) => m.author_type === 'admin');
}

/** The approved-document reply door: an expired live file (today's renewal door) OR, for a compliance document, a reviewer comment in the current round. */
export function approvedDocReplyAllowed(args: {
  isComplianceDoc: boolean;
  hasExpiredLive: boolean;
  roundHasAdminMessage: boolean;
}): boolean {
  return args.hasExpiredLive || (args.isComplianceDoc && args.roundHasAdminMessage);
}
